/**
 * @file Renders one editable table cell, with its input, save and read modes.
 */
import React, { useContext, useEffect, useState } from "react";
import { AutoComplete, Form, Select, Space, Button } from "antd";
import isEqual from "lodash.isequal";
import { SaveOutlined } from "@ant-design/icons";
import type { CSSProperties, ComponentProps, ReactNode, TdHTMLAttributes } from "react";
import type { DefaultOptionType } from "antd/es/select";

import FormItem from "@components/Form/FormItem";

import { EditableContext } from "./Collection";
import type { CellProps } from "./types";

const { Compact } = Space;

const TYPE_SELECT = "select";
const VALIDATE_TRIGGER_CLICK = "onClick";

const COMPACT_STYLE: CSSProperties = { width: "100%", alignItems: "center" };
const AUTO_COMPLETE_ITEM_STYLE: CSSProperties = { width: "100%", marginBottom: 0 };
const AUTO_COMPLETE_STYLE: CSSProperties = { margin: 0 };
const SELECT_STYLE: CSSProperties = { width: "100%", margin: 0 };
const ITEM_STYLE: CSSProperties = { margin: 0 };

type Props = CellProps & Omit<TdHTMLAttributes<HTMLTableCellElement>, "title"> & {
  children?: ReactNode;
};

/**
 * Renders the cell's input, its read-only children, or a hidden cell.
 */
const CollectionCell = ({
  input,
  record,
  isShow = () => true,
  onSave,
  editing = false,
  children,
  editable = false,
  indexRow = 0,
  dataIndex = "",
  ...restProps
}: Props): JSX.Element => {
  const form = useContext(EditableContext);

  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    const hasRecord = !!record;

    if (!hasRecord) {
      return;
    }

    form.setFieldValue([ indexRow, dataIndex ], record[dataIndex]);
  }, [
    form,
    record,
    editing,
    indexRow,
    dataIndex,
  ]);

  /**
   * Saves the row's field values when they changed since the last save.
   */
  const save = async (): Promise<void> => {
    const value = form.getFieldValue([ indexRow, dataIndex ]);
    const formValue = form.getFieldValue(indexRow);

    const newValues = {
      ...record,
      ...formValue,
      [dataIndex]: value,
    };

    const hasChanged = !isEqual(record, newValues);

    if (!hasChanged) {
      return;
    }

    const hasSaveHandler = !!onSave;

    if (!hasSaveHandler) {
      return;
    }

    try {
      setIsLoading(true);
      await onSave(newValues);
      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);
      console.error(error);

    }
  };

  const isDisabled = isLoading || (!editing && editable);

  /**
   * Builds the form item props for this cell's field.
   */
  const getItemProps = () => {
    const { hasAutoComplete, ...itemProps } = input.itemProps || {};

    return {
      ...itemProps,
      name: [ indexRow, dataIndex ],
      style: ITEM_STYLE,
    };
  };

  const shouldHide = !isShow(record);

  if (shouldHide) {
    return (
      <td {...restProps} />
    );
  }

  /**
   * Tells whether the option's value contains the typed query.
   */
  const filterAutoCompleteOption = (query: string, option?: DefaultOptionType): boolean => {
    const optionValue = String(option?.value ?? "");

    const isMatch = optionValue.toLowerCase().includes(query.toLowerCase());

    return isMatch;
  };

  /**
   * Builds the input props, wiring the save handler to the input's events.
   */
  const getInputProps = () => {
    const { disabled: getInputDisabled } = input.inputProps || {};

    const hasDisabledGetter = !!getInputDisabled;

    const defaultParams = {
      disabled: hasDisabledGetter
        ? getInputDisabled(record)
        : isDisabled,
    };

    const isSelect = input.type === TYPE_SELECT;

    if (isSelect) {
      return {
        ...input.inputProps,
        ...defaultParams,
        onChange: save,
      };
    }

    return {
      ...input.inputProps,
      ...defaultParams,
      onBlur: save,
      onPressEnter: save,
    };
  };

  /**
   * Renders the cell's select, autocomplete-select or plain form input.
   */
  const renderInput = (): JSX.Element => {
    const hasAutoComplete = !!input.itemProps?.hasAutoComplete;

    const isSelect = input.type === TYPE_SELECT;

    if (isSelect) {
      if (hasAutoComplete) {
        return (
          <Compact style={COMPACT_STYLE}>
            <Form.Item
              {...getItemProps()}
              validateTrigger={VALIDATE_TRIGGER_CLICK}
              style={AUTO_COMPLETE_ITEM_STYLE}
            >
              <AutoComplete
                style={AUTO_COMPLETE_STYLE}
                options={input.options(record)}
                disabled={isDisabled}
                showSearch={true}
                filterOption={filterAutoCompleteOption}
              />
            </Form.Item>

            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={save}
              loading={isLoading}
              disabled={isDisabled}
            />
          </Compact>
        );
      }

      return (
        <Form.Item
          {...getItemProps()}
          validateTrigger={VALIDATE_TRIGGER_CLICK}
        >
          <Select
            onChange={save}
            style={SELECT_STYLE}
            disabled={isDisabled}
            options={input.options(record)}
            allowClear={input.allowClear}
          />
        </Form.Item>
      );
    }

    // FormItem is a JavaScript component, so each prop without a default
    // reads as required.
    const formItemProps = {
      ...input,
      form,
      path: [ indexRow, dataIndex ],
      itemProps: getItemProps(),
      inputProps: getInputProps(),
    } as unknown as ComponentProps<typeof FormItem>;

    return (
      <FormItem {...formItemProps} />
    );
  };

  const isRowReadOnly =
    !!record &&
    record.editableRow !== undefined &&
    !record.editableRow;

  if (isRowReadOnly) {
    return (
      <td {...restProps}>
        {children}
      </td>
    );
  }

  const shouldRenderInput = editable;
  const shouldRenderChildren = !editing && !editable;

  return (
    <td {...restProps}>
      {shouldRenderInput && renderInput()}

      {shouldRenderChildren && children}
    </td>
  );
};

export default CollectionCell;
